import { ApiClient, ApiError, type AuthMe } from '../lib/api.js';
import { el } from '../lib/dom.js';

export interface LoginViewDeps {
  api: ApiClient;
  onSuccess: (me: AuthMe) => void;
}

export function renderLogin(deps: LoginViewDeps): HTMLElement {
  const errorBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
  const submit = el('button', {
    class: 'primary',
    text: 'Sign in',
    attrs: { type: 'submit' },
  });
  const username = el('input', {
    attrs: {
      id: 'username',
      type: 'text',
      autocomplete: 'username',
      required: true,
      autocapitalize: 'off',
      autocorrect: 'off',
      spellcheck: false,
    },
  });
  const password = el('input', {
    attrs: {
      id: 'password',
      type: 'password',
      autocomplete: 'current-password',
      required: true,
    },
  });

  const form = el('form', {
    on: {
      submit: async (ev) => {
        ev.preventDefault();
        errorBox.setAttribute('hidden', '');
        submit.setAttribute('disabled', '');
        submit.textContent = 'Signing in…';
        try {
          const me = await deps.api.login(username.value.trim(), password.value);
          deps.onSuccess(me);
        } catch (err) {
          submit.removeAttribute('disabled');
          submit.textContent = 'Sign in';
          const message =
            err instanceof ApiError
              ? err.status === 429
                ? 'Too many attempts. Try again later.'
                : err.status === 401
                  ? 'Invalid username or password.'
                  : err.message
              : 'Network error.';
          errorBox.textContent = message;
          errorBox.removeAttribute('hidden');
        }
      },
    },
    children: [
      errorBox,
      el('div', {
        class: 'field',
        children: [
          el('label', { text: 'Username', attrs: { for: 'username' } }),
          username,
        ],
      }),
      el('div', {
        class: 'field',
        children: [
          el('label', { text: 'Password', attrs: { for: 'password' } }),
          password,
        ],
      }),
      submit,
    ],
  });

  // Public API reference — shown as the full absolute URL so a machine caller
  // (e.g. a Claude Code session) can find everything from the login screen
  // alone, without logging in.
  const docsUrl = `${location.origin}/api-docs`;
  const docsLink = el('p', {
    class: 'login-docs',
    children: [
      el('span', { text: 'API for deploy keys: ' }),
      el('a', { text: docsUrl, attrs: { href: '/api-docs' } }),
    ],
  });

  return el('div', {
    class: 'login-wrap',
    children: [
      el('div', {
        class: 'card login-card',
        children: [
          el('h1', { text: 'Blue Eden Deploy' }),
          el('p', { class: 'lead', text: 'Sign in to manage deployments' }),
          form,
          docsLink,
        ],
      }),
    ],
  });
}
